/**
 * Livraisons (A3) : suivi des commandes livrées, ESTIMATION transporteur distincte du TARIF CONFIRMÉ,
 * confirmation du tarif réel, événements de suivi (en transit, incident, livrée). Coûts internes : back-office uniquement.
 */
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, Pagination, SelectField, Table, TextField, TextareaField, useToast, type TonBadge } from '@myron/design-system';
import { CheckCircle2, CircleAlert, Truck } from 'lucide-react';
import { get, post } from '../../../shared/api/client';
import { DecisionOuverte, EntetePage, ErreurFormulaire, EtatRequete, StatutBadge } from '../../../shared/ui/composants';
import { dateHeure, euros } from '../../../shared/ui/format';
import { SelectEntrepot, type Page } from '../logistique/commun';

interface Transport {
  statut: 'ESTIME' | 'CONFIRME' | 'EN_TRANSIT' | 'LIVRE' | 'INCIDENT';
  estimeCentimes: number | null; confirmeCentimes: number | null; ecartCentimes: number | null; tarifAConfirmer: boolean;
  transporteur: string | null; numeroSuivi: string | null; nbPalettes: number; evenements: { date: string; libelle: string }[]; estimationRecalculee: boolean;
}
interface Livraison {
  commandeId: string; reference: string; statutCommande: string; version: number; raisonSociale?: string; entrepriseReference?: string;
  entrepotCode?: string; codePostal: string | null; ville: string | null; fraisFacturesHtCentimes: number; transport: Transport; creeLe: string;
}
type ListeLivraisons = Page<Livraison> & { synthese: { estimeCentimes: number; confirmeCentimes: number; aConfirmer: number; incidents: number } };

export const LIBELLE_TRANSPORT: Record<Transport['statut'], string> = { ESTIME: 'Estimé', CONFIRME: 'Tarif confirmé', EN_TRANSIT: 'En transit', LIVRE: 'Livré', INCIDENT: 'Incident' };
const TON_TRANSPORT: Record<Transport['statut'], TonBadge> = { ESTIME: 'neutre', CONFIRME: 'info', EN_TRANSIT: 'marque', LIVRE: 'succes', INCIDENT: 'erreur' };
export const BadgeTransport = ({ statut }: { statut: Transport['statut'] }) => <Badge ton={TON_TRANSPORT[statut]}>{LIBELLE_TRANSPORT[statut]}</Badge>;

const enCentimes = (v: string) => {
  if (!v.trim()) return null;
  const n = Math.round(Number(v.replace(/\s|€/g, '').replace(',', '.')) * 100);
  return Number.isFinite(n) && n >= 0 ? n : NaN;
};

/** Montants : l'estimation n'est jamais présentée comme le coût réel. */
function Montants({ t }: { t: Transport }) {
  return (
    <>
      <span>Estimé : {euros(t.estimeCentimes)}{t.estimationRecalculee ? ' (recalculé)' : ''}</span><br />
      <span className="my-petit">{t.tarifAConfirmer ? <Badge ton="alerte">Tarif réel à confirmer</Badge> : <>Confirmé : <strong>{euros(t.confirmeCentimes)}</strong>{t.ecartCentimes ? ` (écart ${t.ecartCentimes > 0 ? '+' : ''}${euros(t.ecartCentimes)})` : ''}</>}</span>
    </>
  );
}

export function PageLivraisons() {
  const nav = useNavigate();
  const [entrepotId, setEntrepot] = useState('');
  const [statut, setStatut] = useState('');
  const [aConfirmer, setAConfirmer] = useState(false);
  const [page, setPage] = useState(1);
  const r = useQuery({
    queryKey: ['livraisons', entrepotId, statut, aConfirmer, page],
    queryFn: () => get<ListeLivraisons>('/admin/livraisons', { entrepotId: entrepotId || undefined, statutTransport: statut || undefined, tarifAConfirmer: aConfirmer ? 'true' : undefined, page, taille: 20 }),
  });
  return (
    <>
      <EntetePage titre="Livraisons" sousTitre="Transport sortant : estimation au moment de la commande, tarif réellement confirmé par le transporteur, suivi jusqu’à la livraison." />
      <DecisionOuverte id="DO-22">Flotte propre ou transporteur : non arbitré. Grille d’estimation simulée (palette, messagerie, majoration Corse) ; aucun appel à un transporteur réel.</DecisionOuverte>
      <div className="my-form-ligne" style={{ marginTop: 16 }}>
        <SelectEntrepot valeur={entrepotId} onChange={(v) => { setEntrepot(v); setPage(1); }} />
        <SelectField label="Statut du transport" value={statut} onChange={(e) => { setStatut(e.target.value); setPage(1); }} options={[{ value: '', label: 'Tous' }, ...Object.entries(LIBELLE_TRANSPORT).map(([value, label]) => ({ value, label }))]} />
        <SelectField label="Tarif réel" value={aConfirmer ? 'oui' : ''} onChange={(e) => { setAConfirmer(e.target.value === 'oui'); setPage(1); }} options={[{ value: '', label: 'Tous' }, { value: 'oui', label: 'À confirmer uniquement' }]} />
      </div>
      <EtatRequete requete={r} vide={(d) => !d.donnees.length} titreVide="Aucune livraison" texteVide="Aucune commande en livraison ne correspond à ces filtres." enfants={(d) => (
        <>
          <div className="my-ligne" data-testid="synthese-livraisons" style={{ margin: '16px 0' }}>
            <Badge ton="neutre">{`Estimé : ${euros(d.synthese.estimeCentimes)}`}</Badge>
            <Badge ton="info">{`Confirmé : ${euros(d.synthese.confirmeCentimes)}`}</Badge>
            <Badge ton="alerte">{`${d.synthese.aConfirmer} tarif(s) à confirmer`}</Badge>
            {d.synthese.incidents > 0 && <Badge ton="erreur">{`${d.synthese.incidents} incident(s)`}</Badge>}
          </div>
          <Table<Livraison> legende="Livraisons" lignes={d.donnees} cle={(l) => l.commandeId} onLigne={(l) => nav(`/admin/livraisons/${l.commandeId}`)} colonnes={[
            { cle: 'r', titre: 'Commande', rendu: (l) => <Link to={`/admin/livraisons/${l.commandeId}`} onClick={(e) => e.stopPropagation()}>{l.reference}</Link> },
            { cle: 'c', titre: 'Client', rendu: (l) => <>{l.raisonSociale}<br /><span className="my-legende my-secondaire">{l.codePostal} {l.ville}</span></> },
            { cle: 'e', titre: 'Entrepôt', rendu: (l) => l.entrepotCode },
            { cle: 's', titre: 'Commande', rendu: (l) => <StatutBadge statut={l.statutCommande} /> },
            { cle: 't', titre: 'Transport', rendu: (l) => <BadgeTransport statut={l.transport.statut} /> },
            { cle: 'm', titre: 'Coût transporteur', rendu: (l) => <Montants t={l.transport} /> },
          ]} />
          <Pagination page={d.page} total={d.total} taille={d.taille} onPage={setPage} />
        </>
      )} />
    </>
  );
}

export function PageLivraison() {
  const { commandeId = '' } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const r = useQuery({ queryKey: ['livraisons', 'detail', commandeId], queryFn: () => get<Livraison>(`/admin/livraisons/${commandeId}`) });
  const [tarif, setTarif] = useState('');
  const [transporteur, setTransporteur] = useState('');
  const [statutEvt, setStatutEvt] = useState<'EN_TRANSIT' | 'INCIDENT' | 'LIVRE'>('EN_TRANSIT');
  const [libelle, setLibelle] = useState('');
  const rafraichir = (l: Livraison) => { qc.setQueryData(['livraisons', 'detail', commandeId], l); void qc.invalidateQueries({ queryKey: ['livraisons'] }); };
  const confirmer = useMutation({
    mutationFn: () => post<Livraison>(`/admin/livraisons/${commandeId}/confirmation`, { tarifConfirmeCentimes: enCentimes(tarif), transporteur }, { version: r.data!.version }),
    onSuccess: (l) => { rafraichir(l); setTarif(''); toast({ ton: 'succes', titre: 'Tarif transporteur confirmé' }); },
  });
  const evenement = useMutation({
    mutationFn: () => post<Livraison>(`/admin/livraisons/${commandeId}/evenements`, { statut: statutEvt, ...(libelle.trim() ? { libelle: libelle.trim() } : {}) }, { version: r.data!.version }),
    onSuccess: (l) => { rafraichir(l); setLibelle(''); toast({ ton: statutEvt === 'INCIDENT' ? 'alerte' : 'succes', titre: `Suivi : ${LIBELLE_TRANSPORT[statutEvt]}` }); },
  });
  return (
    <EtatRequete requete={r} enfants={(l) => {
      const t = l.transport;
      const avantExpedition = !['EXPEDIEE', 'LIVREE'].includes(l.statutCommande);
      const tarifSaisi = enCentimes(tarif);
      return (
        <>
          <EntetePage titre={`Livraison ${l.reference}`} sousTitre={`${l.raisonSociale ?? ''} · ${l.codePostal ?? ''} ${l.ville ?? ''} · entrepôt ${l.entrepotCode ?? ''}`} fil={[{ libelle: 'Livraisons', vers: '/admin/livraisons' }, { libelle: l.reference }]}
            actions={<><StatutBadge statut={l.statutCommande} /><BadgeTransport statut={t.statut} /></>} />
          <div className="my-grille-2" style={{ marginTop: 16 }}>
            <Card as="section" className="my-pile" aria-labelledby="titre-couts">
              <h2 id="titre-couts" className="my-titre-4">Coût du transport</h2>
              <dl className="my-dl">
                <dt>Estimation</dt><dd>{euros(t.estimeCentimes)} {t.estimationRecalculee && <span className="my-legende my-secondaire">(recalculée : commande antérieure au module)</span>}</dd>
                <dt>Tarif confirmé</dt><dd>{t.tarifAConfirmer ? <Badge ton="alerte">À confirmer</Badge> : <strong>{euros(t.confirmeCentimes)}</strong>}</dd>
                <dt>Écart</dt><dd>{t.ecartCentimes === null ? '—' : `${t.ecartCentimes > 0 ? '+' : ''}${euros(t.ecartCentimes)}`}</dd>
                <dt>Palettes</dt><dd>{t.nbPalettes || 'messagerie'}</dd>
                <dt>Transporteur</dt><dd>{t.transporteur ?? '—'}{t.numeroSuivi ? ` · suivi ${t.numeroSuivi}` : ''}</dd>
                <dt>Frais facturés au client (HT)</dt><dd>{euros(l.fraisFacturesHtCentimes)}</dd>
              </dl>
              <p className="my-legende my-secondaire">Le coût confirmé (et non l’estimation) alimente le transport sortant supporté par MYRON dans les analyses de rentabilité.</p>
            </Card>

            {avantExpedition ? (
              <Card as="section" className="my-pile" aria-labelledby="titre-confirmation">
                <h2 id="titre-confirmation" className="my-titre-4">Confirmer le tarif réel</h2>
                <p className="my-petit">Saisissez le prix accepté par le transporteur. L’estimation reste conservée pour mesurer l’écart.</p>
                <div className="my-form-ligne">
                  <TextField label="Tarif confirmé HT (€)" inputMode="decimal" value={tarif} onChange={(e) => setTarif(e.target.value)} erreur={Number.isNaN(tarifSaisi) ? 'Montant invalide' : undefined} required />
                  <TextField label="Transporteur" value={transporteur} onChange={(e) => setTransporteur(e.target.value)} required />
                </div>
                <ErreurFormulaire erreur={confirmer.error} />
                <Button icone={CheckCircle2} disabled={tarifSaisi === null || Number.isNaN(tarifSaisi) || transporteur.trim().length < 2} chargement={confirmer.isPending} onClick={() => confirmer.mutate()}>Confirmer le tarif</Button>
                <p className="my-legende my-secondaire">L’expédition se déclare dans <Link to="/admin/remises">Retraits et expéditions</Link> (le tarif peut aussi y être saisi).</p>
              </Card>
            ) : (
              <Card as="section" className="my-pile" aria-labelledby="titre-suivi">
                <h2 id="titre-suivi" className="my-titre-4">Ajouter un événement de suivi</h2>
                <SelectField label="Événement" value={statutEvt} onChange={(e) => setStatutEvt(e.target.value as typeof statutEvt)} options={[{ value: 'EN_TRANSIT', label: 'En transit' }, { value: 'INCIDENT', label: 'Incident de transport' }, { value: 'LIVRE', label: 'Livrée' }]} />
                <TextareaField label={statutEvt === 'INCIDENT' ? 'Description de l’incident (obligatoire)' : 'Commentaire'} value={libelle} onChange={(e) => setLibelle(e.target.value)} />
                {statutEvt === 'INCIDENT' && <Alert ton="alerte">Le client est informé par e-mail de l’incident.</Alert>}
                <ErreurFormulaire erreur={evenement.error} />
                <Button icone={statutEvt === 'INCIDENT' ? CircleAlert : Truck} disabled={statutEvt === 'INCIDENT' && libelle.trim().length < 5} chargement={evenement.isPending} onClick={() => evenement.mutate()}>Enregistrer l’événement</Button>
              </Card>
            )}
          </div>
          <section className="my-pile" style={{ marginTop: 24 }} aria-labelledby="titre-evenements">
            <h2 id="titre-evenements" className="my-titre-4">Historique du transport</h2>
            {t.evenements.length ? (
              <ol className="my-pile my-pile--serre" style={{ paddingLeft: 18 }}>
                {t.evenements.map((e) => <li key={`${e.date}-${e.libelle}`}><span className="my-petit my-secondaire">{dateHeure(e.date)}</span> — {e.libelle}</li>)}
              </ol>
            ) : <p className="my-secondaire">Aucun événement : transport encore au stade de l’estimation.</p>}
          </section>
        </>
      );
    }} />
  );
}
